import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { FC, useEffect, useRef, useState } from "react";

import { UploadPhotoItem } from "@/types/common";
import { getFileUrl } from "@/utils/file/getFileUrl";

import { PhotoEditorModal } from "../Modals/PhotoEditorModal";
import {
    StyledAddButton,
    StyledPhotoGrid,
    StyledThumbContainer,
} from "./Styled";

interface PhotoUploadInputProps {
    value?: UploadPhotoItem[];
    onChange?: (items: UploadPhotoItem[]) => void;
    buttonLabel?: string;
}

const createUid = () =>
    `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getOrCreateBlobUrl = (
    map: Map<string, string>,
    uid: string,
    file: File,
) => {
    if (!map.has(uid)) {
        map.set(uid, URL.createObjectURL(file));
    }

    return map.get(uid)!;
};

const getPreviewUrl = (item: UploadPhotoItem, blobMap: Map<string, string>) => {
    if (item.kind === "url") return getFileUrl(item.url);

    return getOrCreateBlobUrl(blobMap, item.uid, item.file);
};

export const PhotoUploadInput: FC<PhotoUploadInputProps> = ({
    value = [],
    onChange,
    buttonLabel = "Додати фото",
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const blobMapRef = useRef(new Map<string, string>());

    const [editorState, setEditorState] = useState<{
        item: UploadPhotoItem;
        index: number;
    } | null>(null);

    useEffect(() => {
        const map = blobMapRef.current;

        return () => {
            map.forEach((url) => URL.revokeObjectURL(url));
            map.clear();
        };
    }, []);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);

        const newItems: UploadPhotoItem[] = files.map((file) => ({
            kind: "file",
            file,
            uid: createUid(),
        }));

        onChange?.([...value, ...newItems]);

        e.target.value = "";
    };

    const handleRemove = (index: number) => {
        const item = value[index];

        if (item?.kind === "file") {
            const url = blobMapRef.current.get(item.uid);

            if (url) {
                URL.revokeObjectURL(url);
                blobMapRef.current.delete(item.uid);
            }
        }

        onChange?.(value.filter((_, i) => i !== index));
    };

    const handleEditorSave = (newFile: File, index: number) => {
        const uid = createUid();

        onChange?.(
            value.map((item, i) =>
                i === index ? { kind: "file", file: newFile, uid } : item,
            ),
        );

        setEditorState(null);
    };

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFileInput}
                style={{ display: "none" }}
            />

            <StyledPhotoGrid>
                {value.map((item, index) => {
                    const previewUrl = getPreviewUrl(item, blobMapRef.current);
                    const key = item.kind === "url" ? `url-${index}` : item.uid;

                    return (
                        <StyledThumbContainer key={key}>
                            <img src={previewUrl} alt={`photo-${index}`} />

                            <div className="actions">
                                <EditOutlined
                                    onClick={() =>
                                        setEditorState({ item, index })
                                    }
                                />

                                <DeleteOutlined
                                    onClick={() => handleRemove(index)}
                                />
                            </div>
                        </StyledThumbContainer>
                    );
                })}

                <StyledAddButton
                    type="button"
                    onClick={() => inputRef.current?.click()}
                >
                    <PlusOutlined />
                    <span>{buttonLabel}</span>
                </StyledAddButton>
            </StyledPhotoGrid>

            {editorState && (
                <PhotoEditorModal
                    item={editorState.item}
                    index={editorState.index}
                    onClose={() => setEditorState(null)}
                    onSave={handleEditorSave}
                />
            )}
        </div>
    );
};
